import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
	deliverContactRequest,
	isAllowedContactRequest,
} from "@/lib/contact.server";
import { localeSchema } from "@/lib/i18n/locale";

/** Needs offered in the form, with the `?besoin=` slug used by offer pages. */
export const CONTACT_NEEDS = {
	businessApps: "applications-metier",
	expoMigration: "migration-expo",
	other: "autre",
} as const;

export type ContactNeed = keyof typeof CONTACT_NEEDS;
export type ContactNeedSlug = (typeof CONTACT_NEEDS)[ContactNeed];

export const contactNeedSchema = z.enum([
	"businessApps",
	"expoMigration",
	"other",
]);
export const contactNeedSlugSchema = z.enum([
	"applications-metier",
	"migration-expo",
	"autre",
]);

export const needFromSlug = (slug?: string): ContactNeed | undefined =>
	(Object.keys(CONTACT_NEEDS) as ContactNeed[]).find(
		(need) => CONTACT_NEEDS[need] === slug,
	);

/** Needs worth a call right away; other requests get a reply by email. */
export const BOOKABLE_NEEDS: ReadonlySet<ContactNeed> = new Set([
	"businessApps",
	"expoMigration",
]);

/** Below this delay, a submission is treated as automated. */
export const MIN_FILL_DURATION_MS = 3000;

/** Line breaks have no place in short fields, and could reach mail headers. */
const singleLine = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.refine((value) => !/[\r\n]/.test(value));

export const contactFieldsSchema = z.object({
	name: singleLine(120).pipe(z.string().min(1)),
	email: z.email().trim().max(200),
	company: singleLine(160).pipe(z.string().min(1)),
	need: contactNeedSchema,
	situation: z.string().trim().min(20).max(5000),
	timeline: singleLine(200),
	budget: singleLine(200),
	website: singleLine(300).refine(
		(value) => value === "" || /^https?:\/\/\S+$/i.test(value),
	),
});

export type ContactFields = z.infer<typeof contactFieldsSchema>;
export type ContactField = keyof ContactFields;

export const contactRequestSchema = contactFieldsSchema.extend({
	locale: localeSchema,
	/** Honeypot: hidden from people, filled by naive bots. */
	nickname: z.string().max(200),
	elapsedMs: z.number().int().nonnegative(),
});

export type ContactRequest = z.infer<typeof contactRequestSchema>;

export const submitContactRequest = createServerFn({ method: "POST" })
	.inputValidator(contactRequestSchema)
	.handler(async ({ data }) => {
		const isAutomated =
			data.nickname !== "" || data.elapsedMs < MIN_FILL_DURATION_MS;
		// Bots get the same answer as people, without anything being sent.
		if (isAutomated) return { ok: true as const };
		if (!isAllowedContactRequest()) return { ok: false as const };
		const delivered = await deliverContactRequest(data);
		return delivered ? { ok: true as const } : { ok: false as const };
	});
